import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop } from '@Ravnur-Inc/decorators';
import isEqual from '@Ravnur-Inc/nanoutils/isEqual';
import keyBy from '@Ravnur-Inc/nanoutils/keyBy';
import sortBy from '@Ravnur-Inc/nanoutils/sortBy';
import { Vue, Options, setup } from 'vue-class-component';
import isSame from '@Ravnur-Inc/nanoutils/isSame';
import some from '@Ravnur-Inc/nanoutils/some';
import MetadataList from '@/modules/metadata/components/metadata-list';
import {
  metadataFieldsService,
  metadataTemplatesService,
} from '@/modules/metadata/metadata-facade';
import { Metadata, Metadata$Field, Metadata$Template } from '@/modules/metadata/types/Metadata';
import MediaMetadataRepository from '@/repositories/media-metadata-repository';
import { Media$Details, SINGLE_MEDIA_TYPES } from '@/types/Media';
import { useVuelidate } from '@vuelidate/core';
import PopupButton from '@/components/popup-button/popup-button';
import AddMetadataFieldsModal from '@/modules/metadata/components/add-metadata-fields-modal';
import uidGenerator from '@/helpers/uid-generator';
import { languagesService } from '@/modules/common-services';
import ChipsInput from '@/components/chips-input/chips-input';
import useSecurityStore from '@/store/security';
import { TagEntity } from '@/types/Tag';
import CategoriesTreeModal from '@/components/category/categories-modal/categories-modal';
import { Category } from '@/types/Category';
import Chips from '@Ravnur-Inc/core/components/chips/chips';
import MediaRepository from '@/repositories/media-repository';
import TagsRepository from '@/repositories/tags-repository';
import LookupRepository from '@/repositories/lookup-repository';
import { LookupEntity$Type } from '@/types/LookupEntity';

import './media-metadata.scss';

const CN = 'media-metadata';
const sortByName = sortBy((field: Metadata$Field | Metadata$Template) => field.name);
const mediaRepository = new MediaRepository();
const tagsRepository = new TagsRepository();

type Props = {
  origin: Media$Details;
};

@Options({
  watch: {
    mediaId: {
      immediate: true,
      handler(this: MediaMetadata) {
        this.loadMetadata();
      },
    },
    templateId: {
      handler(this: MediaMetadata) {
        this.addTemplate();
      },
    },
  },
})
export default class MediaMetadata extends Vue {
  @Prop({}) public readonly origin!: Media$Details;

  declare $props: $Types<Props>;

  private v$ = setup(() => useVuelidate());
  private security = setup(() => useSecurityStore());

  private loading = false;
  private list: Array<Unsaved<Metadata>> = [];
  private originMetadata: Metadata[] = [];
  private languageId = '';
  private tags: TagEntity[] = [];
  private categories: Category[] = [];

  private templateId: Nullable<string> = null;

  get mediaId() {
    return this.origin.id;
  }

  get localForm() {
    return {
      ...this.origin,
      languageId: this.languageId,
      tags: this.tags,
      categories: this.categories,
    };
  }

  private get repository() {
    return new MediaMetadataRepository(this.origin);
  }

  private get templates() {
    return sortByName(metadataTemplatesService.list);
  }

  private get isChanged() {
    return this.isMediaChanged || this.isMetadataChanged;
  }

  private get isMetadataChanged() {
    const { originMetadata, list } = this;
    return !isEqual(originMetadata, list);
  }

  private get isMediaChanged() {
    return !isEqual(this.origin, this.localForm);
  }

  private get languages() {
    return languagesService.list;
  }

  private get isSingleMedia() {
    return some(isSame(this.origin.type), SINGLE_MEDIA_TYPES);
  }

  private get selectedMetadataFields(): Metadata$Field[] {
    return metadataFieldsService.list.filter((f) => this.list.some((l) => l.fieldId === f.id));
  }

  private set selectedMetadataFields(fields: Metadata$Field[]) {
    this.list = fields.map((f) => {
      const existing = this.list.find((l) => l.fieldId === f.id);

      if (existing) {
        return existing;
      }

      const defaultValue = f.predefinedValues.find((v) => v.isDefault);
      const metadata = {
        id: uidGenerator(),
        fieldId: f.id,
        value: defaultValue?.value ?? '',
      };

      return metadata;
    });
  }

  private get lookupTagRepository() {
    return new LookupRepository(LookupEntity$Type.TAGS);
  }

  render() {
    return (
      <div class={CN}>
        <div class={`${CN}__media-info`}>
          <chosen
            class={`${CN}__field`}
            keyForLabel="localName"
            label={this.$t('media', 'field__metadata-language')}
            options={this.languages}
            v-show={this.isSingleMedia} //hide languages for playlsits
            vModel={[this.languageId, 'value']}
          />

          <div class={`${CN}__media-info--chips`}>
            {this.security.isManageTagsEnabled && (
              <ChipsInput
                class={`${CN}__media-info--tags`}
                createRepository={tagsRepository}
                creator={true}
                label={this.$t('media', 'field__tags')}
                propName="name"
                repository={this.lookupTagRepository}
                v-show={this.isSingleMedia && this.security.isManageTagsEnabled}
                vModel={[this.tags, 'items']}
              />
            )}

            {this.security.isCategoriesAvailable && this.isSingleMedia && (
              <div class={`${CN}__media-info--categories`}>
                <l10n class={`${CN}__label`} group="media" tkey="field__categories" />
                <PopupButton
                  class={`${CN}__media-info--categories-btn`}
                  icon="edit"
                  mode="frameless"
                  size="sm"
                  text={this.$t('media', 'action__edit')}
                >
                  <CategoriesTreeModal vModel={[this.categories, 'selected']} />
                </PopupButton>

                <Chips
                  class={`${CN}__categories`}
                  propName="name"
                  vModel={[this.categories, 'items']}
                />
              </div>
            )}
          </div>
        </div>
        <div class={`${CN}__info-label`}>
          <l10n group="media" tkey="metadata-list__info-label" />
        </div>
        <div class={`${CN}__info`}>{this.$t('media', 'metadata-list__info')}</div>
        <div class={`${CN}__add-section`}>
          <div class={`${CN}__row`}>
            <chosen
              class={`${CN}__templates`}
              keyForLabel="name"
              label={this.$t('media', 'metadata-list__template')}
              options={this.templates}
              placeholder={this.$t('media', 'metadata-list__template-selector-placeholder')}
              vModel={[this.templateId, 'value']}
            />
            <PopupButton
              color="primary"
              text={this.$t('media', 'metadata-field__add-new-metadata')}
            >
              <AddMetadataFieldsModal
                fields={metadataFieldsService.list}
                vModel={[this.selectedMetadataFields, 'value']}
              />
            </PopupButton>
          </div>
        </div>

        <MetadataList key={this.loading.toString()} vModel={[this.list, 'metadata']} />

        <div class={`${CN}__actions`}>
          <r-button class={`${CN}__btn`} mode="borderless" onclick={this.cancel}>
            <l10n group="common" tkey="action__cancel" />
          </r-button>

          <action-button
            class={`${CN}__btn`}
            color="primary"
            disabled={!this.isChanged}
            process={this.save}
          >
            <l10n group="common" tkey="action__save" />
          </action-button>
        </div>

        {this.loading ? <spinner /> : null}
      </div>
    );
  }

  async addTemplate() {
    const { templateId, list } = this;
    if (!templateId) {
      return;
    }

    const template = metadataTemplatesService.cache[templateId];

    if (!template) {
      return;
    }

    try {
      const cache = keyBy('fieldId', list);
      const ids = template.fieldIds;
      const metadata = ids
        .filter((id) => !cache[id])
        .map((fieldId) => ({ id: uidGenerator(), value: '', fieldId }));
      this.list = list.concat(metadata);
      this.templateId = null;
    } catch (e) {
      this.$processException(e);
    }
  }

  private cancel() {
    this.list = this.originMetadata.map((m) => ({ ...m }));
    this.$nextTick(() => {
      this.$emit('to-list');
    });
  }

  async saveMetadata() {
    if (!(await this.v$.$validate?.())) {
      return;
    }

    try {
      await this.repository.saveMany(this.list);
      this.loadMetadata();
    } catch (e) {
      this.$processException(e);
    }
  }

  async saveMedia() {
    try {
      await mediaRepository.save(this.localForm);
      this.$emit('stored', this.localForm);
    } catch (e) {
      this.$processException(e);
    }
  }

  async save() {
    const requests = [];

    if (this.isMetadataChanged) {
      requests.push(this.saveMetadata());
    }

    if (this.isMediaChanged) {
      requests.push(this.saveMedia());
    }

    if (requests.length) {
      await Promise.all(requests);
    }
  }

  async loadMetadata() {
    this.loading = true;

    try {
      const list = await this.repository.load();
      this.originMetadata = list;
      this.list = list.map((m) => ({ ...m }));
    } catch (e) {
      this.$processException(e);
    }

    this.loading = false;
  }

  refresh() {
    this.languageId = this.origin.languageId;
    this.tags = [...this.origin.tags];
    this.categories = [...this.origin.categories];
  }

  created() {
    // RMP-2985: Forcing service to clean old values.
    metadataFieldsService.logout();
    metadataTemplatesService.logout();

    metadataFieldsService.load();
    metadataTemplatesService.load();
  }

  mounted() {
    this.refresh();
  }
}
